import type { SupabaseClient } from '../../lib/supabase';
import type { ParsedItem } from './parseReport';

export interface InventorySnapshot {
  id: string;
  supplier_organization_id: string;
  report_date: string;
  revision: number;
  is_active: boolean;
  is_locked: boolean;
  uploaded_by: string | null;
  created_at: string;
}

export interface InventoryItem {
  id: string;
  snapshot_id: string;
  sku: string;
  description: string;
  unit_of_measure: string;
  stock_qty: number;
  category: 'spool' | 'bead' | 'charm';
  position: number;
}

const SNAPSHOT_COLUMNS =
  'id, supplier_organization_id, report_date, revision, is_active, is_locked, uploaded_by, created_at';
const ITEM_COLUMNS =
  'id, snapshot_id, sku, description, unit_of_measure, stock_qty, category, position';

/**
 * Which supplier the Materials tab should open on.
 *
 * It used to open on whichever organisation sorted first by name, which is a
 * fine default only if that supplier has a report. On staging it did not:
 * Carnaval sorts first and has none, while the only stock report belongs to
 * another org — so everyone except the person who uploaded it opened the tab,
 * saw an empty panel, and reasonably concluded the upload was invisible to
 * them. It was a picker default, not a permission.
 *
 * Returns null when nothing has been uploaded at all, which is a real empty
 * state rather than a wrong supplier.
 */
export async function fetchOrganizationWithLatestReport(
  client: SupabaseClient,
): Promise<string | null> {
  const { data, error } = await client
    .from('inventory_snapshots')
    .select('supplier_organization_id, report_date')
    .eq('is_active', true)
    .order('report_date', { ascending: false })
    .limit(1)
    .maybeSingle();
  if (error) {
    throw new Error(error.message);
  }
  return data?.supplier_organization_id ?? null;
}

/** The snapshot people should be reading: newest report date, active revision. */
export async function fetchLatestSnapshot(
  client: SupabaseClient,
  supplierOrganizationId: string,
): Promise<InventorySnapshot | null> {
  const { data, error } = await client
    .from('inventory_snapshots')
    .select(SNAPSHOT_COLUMNS)
    .eq('supplier_organization_id', supplierOrganizationId)
    .eq('is_active', true)
    .order('report_date', { ascending: false })
    .limit(1)
    .maybeSingle();
  if (error) {
    throw new Error(error.message);
  }
  return data ?? null;
}

export async function fetchSnapshotHistory(
  client: SupabaseClient,
  supplierOrganizationId: string,
): Promise<InventorySnapshot[]> {
  const { data, error } = await client
    .from('inventory_snapshots')
    .select(SNAPSHOT_COLUMNS)
    .eq('supplier_organization_id', supplierOrganizationId)
    .order('report_date', { ascending: false })
    .order('revision', { ascending: false });
  if (error) {
    throw new Error(error.message);
  }
  return data ?? [];
}

export async function fetchSnapshotItems(
  client: SupabaseClient,
  snapshotId: string,
): Promise<InventoryItem[]> {
  const { data, error } = await client
    .from('inventory_items')
    .select(ITEM_COLUMNS)
    .eq('snapshot_id', snapshotId)
    .order('position');
  if (error) {
    throw new Error(error.message);
  }
  // The column is text in the database; a check constraint restricts it to the
  // three categories, so narrowing here reflects a guarantee rather than a hope.
  return (data ?? []) as InventoryItem[];
}

/**
 * Saves a parsed report as a new snapshot.
 *
 * A report for a month that already has one becomes the next revision and
 * takes over as active — suppliers recount and resend, and refusing the
 * correction would push the real numbers back into email.
 *
 * The previous revision is stood down BEFORE the new one is inserted, because
 * a partial unique index allows only one active revision per month. Doing it
 * the other way round would collide with that index and fail.
 */
export async function saveSnapshot(
  client: SupabaseClient,
  input: {
    supplierOrganizationId: string;
    reportDate: string;
    items: ParsedItem[];
    uploadedBy: string;
  },
): Promise<InventorySnapshot> {
  const { data: existing, error: readError } = await client
    .from('inventory_snapshots')
    .select('id, revision')
    .eq('supplier_organization_id', input.supplierOrganizationId)
    .eq('report_date', input.reportDate)
    .order('revision', { ascending: false });
  if (readError) {
    throw new Error(readError.message);
  }

  const rows = (existing ?? []) as unknown as { id: string; revision: number }[];
  const nextRevision = (rows[0]?.revision ?? 0) + 1;

  if (rows.length > 0) {
    const { error: standDownError } = await client
      .from('inventory_snapshots')
      .update({ is_active: false })
      .eq('supplier_organization_id', input.supplierOrganizationId)
      .eq('report_date', input.reportDate);
    if (standDownError) {
      throw new Error(standDownError.message);
    }
  }

  const { data: snapshot, error: insertError } = await client
    .from('inventory_snapshots')
    .insert({
      supplier_organization_id: input.supplierOrganizationId,
      report_date: input.reportDate,
      revision: nextRevision,
      is_active: true,
      uploaded_by: input.uploadedBy,
    })
    .select(SNAPSHOT_COLUMNS)
    .single();
  if (insertError) {
    throw new Error(insertError.message);
  }

  const created = snapshot;
  const { error: itemsError } = await client.from('inventory_items').insert(
    input.items.map((item, index) => ({
      snapshot_id: created.id,
      sku: item.sku,
      description: item.description,
      unit_of_measure: item.unitOfMeasure,
      stock_qty: item.stockQty,
      category: item.category,
      position: index,
    })),
  );
  if (itemsError) {
    throw new Error(itemsError.message);
  }

  // Locked only once the rows are safely in: locking first would leave a
  // failed insert with an empty snapshot nobody could fill or fix.
  const { error: lockError } = await client
    .from('inventory_snapshots')
    .update({ is_locked: true })
    .eq('id', created.id);
  if (lockError) {
    throw new Error(lockError.message);
  }

  return { ...created, is_locked: true };
}

/**
 * Every active snapshot's stock for a supplier, oldest first, so burn rates
 * can be computed per SKU.
 *
 * Only ACTIVE revisions: a superseded revision is a corrected report, and
 * treating a correction as a separate month would invent a consumption period
 * that never happened.
 */
export async function fetchStockHistory(
  client: SupabaseClient,
  supplierOrganizationId: string,
): Promise<Map<string, { reportDate: string; stockQty: number }[]>> {
  const { data, error } = await client
    .from('inventory_items')
    .select(
      'sku, stock_qty, inventory_snapshots!inner(report_date, is_active, supplier_organization_id)',
    )
    .eq('inventory_snapshots.supplier_organization_id', supplierOrganizationId)
    .eq('inventory_snapshots.is_active', true);
  if (error) {
    throw new Error(error.message);
  }

  const history = new Map<string, { reportDate: string; stockQty: number }[]>();
  for (const row of (data ?? []) as unknown as {
    sku: string;
    stock_qty: number;
    inventory_snapshots: { report_date: string };
  }[]) {
    const points = history.get(row.sku) ?? [];
    points.push({ reportDate: row.inventory_snapshots.report_date, stockQty: row.stock_qty });
    history.set(row.sku, points);
  }
  return history;
}
